import api from '../../../utils/api';
import { weatherService } from '../../map/services/weatherService';

export const huntService = {
  // Get all hunts for the current user
  getHunts: async () => {
    const response = await api.get('/hunts/');
    return response.data;
  },

  // Get hunt statistics
  getHuntStats: async () => {
    const response = await api.get('/hunts/stats/');
    return response.data;
  },

  /**
   * Deer sightings heatmap data for personal map.
   * @param {{ start?: string, end?: string }} params - Optional YYYY-MM-DD range. Omit for all time.
   * @returns {{ date_range: { start: string, end: string }, points: Array<{ lat, lng, deer_seen, date }> }}
   */
  getDeerSightingsHeatmap: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.start) qs.set('start', params.start);
    if (params.end) qs.set('end', params.end);
    const url = qs.toString() ? `/hunts/deer_sightings_heatmap/?${qs}` : '/hunts/deer_sightings_heatmap/';
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Deer sightings heatmap data for club map.
   * @param {number} clubId
   * @param {{ start?: string, end?: string }} params - Optional YYYY-MM-DD range. Omit for all time.
   * @returns {{ date_range: { start: string, end: string }, points: Array<{ lat, lng, deer_seen, date }> }}
   */
  getClubDeerSightingsHeatmap: async (clubId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.start) qs.set('start', params.start);
    if (params.end) qs.set('end', params.end);
    const url = qs.toString() ? `/clubs/${clubId}/deer_sightings_heatmap/?${qs}` : `/clubs/${clubId}/deer_sightings_heatmap/`;
    const response = await api.get(url);
    return response.data;
  },

  // Create a new hunt with weather data
  createHunt: async (huntData, coordinates = null) => {
    let weatherData = huntData.weather_data || {}; // Use existing weather data if available
    
    // Only fetch weather if we don't have it and coordinates are provided
    if (!weatherData || Object.keys(weatherData).length === 0) {
      if (coordinates && huntData.time_periods && huntData.time_periods.length > 0) {
        try {
          weatherData = await weatherService.getHuntWeatherData(
            coordinates.lat,
            coordinates.lng,
            huntData.date,
            huntData.time_periods
          );
        } catch (error) {
          console.warn('Failed to fetch weather data for hunt:', error);
          // Continue with hunt creation even if weather fetch fails
        }
      }
    }
    
    // Add weather data to hunt data
    const huntDataWithWeather = {
      ...huntData,
      weather_data: weatherData
    };
    
    const response = await api.post('/hunts/', huntDataWithWeather);
    return response.data;
  },

  // Update an existing hunt
  updateHunt: async (huntId, huntData) => {
    const response = await api.put(`/hunts/${huntId}/`, huntData);
    return response.data;
  },

  // Delete a hunt
  deleteHunt: async (huntId) => {
    const response = await api.delete(`/hunts/${huntId}/`);
    return response.data;
  },

  // Get a specific hunt
  getHunt: async (huntId) => {
    const response = await api.get(`/hunts/${huntId}/`);
    return response.data;
  },

  // Create or update harvest details
  createHarvest: async (harvestData) => {
    const response = await api.post('/harvests/', harvestData);
    return response.data;
  },

  // Update harvest details
  updateHarvest: async (harvestId, harvestData) => {
    const response = await api.put(`/harvests/${harvestId}/`, harvestData);
    return response.data;
  },

  // Delete harvest details
  deleteHarvest: async (harvestId) => {
    const response = await api.delete(`/harvests/${harvestId}/`);
    return response.data;
  },

  // Initialize harvest photo upload (returns presigned PUT URLs and keys)
  initHarvestPhotoUpload: async (harvestId, filename, contentType = 'image/jpeg', sha256) => {
    const payload = {
      scope: 'harvest-photo',
      harvest_id: harvestId,
      filename,
      content_type: contentType
    };
    if (sha256) payload.sha256 = sha256;
    const response = await api.post('/assets/init/', payload);
    return response.data;
  },

  // Attach photo to harvest (supports either asset_id or object keys)
  attachHarvestPhoto: async (harvestId, payload) => {
    // payload may include: asset_id OR object_key + thumb_object_key, and optional caption/width/height/size_bytes
    const response = await api.patch(`/harvests/${harvestId}/photo/`, payload);
    return response.data;
  },

  // Delete harvest photo
  deleteHarvestPhoto: async (harvestId) => {
    const response = await api.delete(`/harvests/${harvestId}/photo/`);
    return response.data;
  }
};
